import { Component, OnInit, OnDestroy, effect } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RouterOutlet } from '@angular/router';
import { PageSidebarMenuComponent } from '../_shared/components/page-sidebar/page-sidebar-menu.component';
import { ClientSidebarComponent } from './_shared/client-sidebar/client-sidebar.component';
import { KycBannerComponent } from './_shared/kyc-banner/kyc-banner.component';
import { ClientHeaderComponent } from './_shared/client-header/client-header.component';
import { MatDialogModule } from '@angular/material/dialog';
import { PageSidebarService } from '../_shared/services/page-sidebar.service';
import { EchoService } from '../_shared/services/echo.service';
import { IntercomService } from '../_shared/services/intercom.service';
import { PersonService } from '../_shared/services/person.service';
import { environment } from 'src/environments/environment';

import { ScrollTopOnNavigateDirective } from '../_shared/directives/scroll-top-on-navigate.directive';
@UntilDestroy()
@Component({
    selector: 'app-client-layout',
    templateUrl: './client-layout.component.html',
    styleUrls: ['./client-layout.component.scss'],
    standalone: true,
    imports: [
        ClientHeaderComponent,
        KycBannerComponent,
        PageSidebarMenuComponent,
        ClientSidebarComponent,
        RouterOutlet,
        MatDialogModule,
        ScrollTopOnNavigateDirective,
    ],
})
export class ClientLayoutComponent implements OnInit, OnDestroy {
    /** The login the Messenger identity was last requested for, so it is fetched once. */
    private intercomRequestedFor: string | null = null;

    constructor(
        public pageSidebarService: PageSidebarService,
        private echoService: EchoService,
        private intercomService: IntercomService,
        private personService: PersonService,
    ) {
        effect(() => {
            const person = this.personService.loggedInUser();
            if (
                environment.production &&
                (person?.isAdmin ||
                    person?.isPropertyManager ||
                    person?.isMaintenance)
            ) {
                this.bootIntercom(
                    person.userId,
                    person.fullName,
                    person.email ?? undefined,
                );
            } else {
                // Signed out, or a login the Messenger isn't for: end any session a
                // previous login left booted, so it can't be read by this one.
                this.endIntercom();
            }
        });
    }

    /**
     * The Messenger identity comes from GET /me/intercom (RF-2683), keyed by the login;
     * the token is only issued where the login is entitled, so a null one leaves the
     * Messenger unbooted.
     *
     * The service is a root singleton and outlives a sign-in. A session booted for a
     * different login is shut down before anything else, so one person never sees
     * another's conversations after switching logins in the same tab.
     */
    private bootIntercom(
        userId: string,
        name: string,
        email: string | undefined,
    ): void {
        if (
            this.intercomService.bootedFor() === userId ||
            this.intercomRequestedFor === userId
        ) {
            return;
        }
        this.intercomService.shutdown();
        this.intercomRequestedFor = userId;

        this.intercomService
            .getIdentity()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (identity) => {
                    // The login changed while this was in flight: it isn't theirs.
                    if (
                        this.intercomRequestedFor !== userId ||
                        !identity.token
                    ) {
                        return;
                    }
                    this.intercomService.boot({
                        userId: identity.userId,
                        name,
                        email,
                        createdAt: identity.createdAt ?? 0,
                        intercomToken: identity.token,
                    });
                },
                // The Messenger is a convenience: a failed lookup leaves it off and
                // lets the next sign-in try again.
                error: () => (this.intercomRequestedFor = null),
            });
    }

    private endIntercom(): void {
        this.intercomRequestedFor = null;
        this.intercomService.shutdown();
    }

    ngOnInit(): void {
        // Initialize Ably/Echo WebSocket connection for real-time updates
        this.echoService.setupEcho();
    }

    ngOnDestroy(): void {
        this.intercomService.shutdown();
    }
}
